import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { UpdateRiskAssessmentResultConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps tRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
  Version: number;
  IsLatest: boolean;
}): UpdateRiskAssessmentResultConfigMutation => ({
  updateRiskAssessmentResultConfigApi: {
    __typename: 'UpdateRiskAssessmentResultConfigApiOutput',
    Id: trpcData.Id,
    Version: trpcData.Version,
    IsLatest: trpcData.IsLatest,
  },
});

export const useUpdateRiskAssessmentResultConfigTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResultConfig.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getLatest.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getByIds.queryKey(),
        });
      },
    }),
  });

  return {
    mutate: async (variables: {
      variables: {
        Id: string;
        Config: RiskAssessmentResultConfig;
        OriginalTimestamp: string;
      };
    }): Promise<UpdateRiskAssessmentResultConfigMutation> => {
      const result = await mutation.mutateAsync({
        Id: variables.variables.Id,
        Config: variables.variables.Config,
        OriginalTimestamp: variables.variables.OriginalTimestamp,
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
